import { useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon, DocumentDuplicateIcon, PhotoIcon } from '@heroicons/react/24/outline';

import { validarDNIoNIF, validarTelefono, buscarDNIEnBaserow } from '../../utils';
import { useAddressAutocomplete } from '../../hooks/useAddressAutocomplete';
import { buscarNegocioEnHubspot, generarDocumentos, descargarGenerados, enviarAFirma, esPdf, TIPO_FORMULARIO } from '../../api/brechaGenero';
import { FormHeader, FormSection, TextField, SearchField, DateField, SelectField, FileField, FileListField, CheckboxField, SubmitButton } from './components/FormFields';
import SubidaLibroFamilia from './components/SubidaLibroFamilia';
import ResumenLectura from './components/ResumenLectura';
import RevisionDocumentos from './components/RevisionDocumentos';
import PanelDocumentos from './components/PanelDocumentos';
import VisorImagenes from './components/VisorImagenes';

// La rejilla del C-155 tiene 6 columnas de hijos.
const MAX_HIJOS = 6;

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// Casilla NO EXISTE / FALLECIDO del otro progenitor. Son excluyentes: en el PDF
// las dos cajitas de una misma columna son el mismo campo.
const ESTADOS_PROGENITOR = [
    { value: 'no_existe', label: 'No existe' },
    { value: 'fallecido', label: 'Fallecido' },
];

// "Indique que complemento solicita": las dos opciones son el mismo campo del
// PDF con dos cajitas, o sea excluyentes.
const COMPLEMENTOS = [
    { value: 'brecha_genero', label: 'Brecha de género' },
    { value: 'maternidad', label: 'Maternidad' },
];

const SEXOS = [
    { value: 'H', label: 'H' },
    { value: 'M', label: 'M' },
];

// Los dos bloques de consentimiento de la hoja 2. No se pintan en pantalla
// porque no son una decisión del agente: siempre se responde lo mismo.
//   - consultar y recabar datos           -> SÍ
//   - usar el móvil y el correo del INSS  -> NO
const CONSENTIMIENTOS = {
    consentimiento_consulta_datos: 'si',
    consentimiento_comunicaciones: 'no',
};

const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** El impreso quiere las fechas en dd/mm/aaaa; los <input type="date"> dan aaaa-mm-dd. */
const aFechaEspanola = (iso) => {
    if (!iso) return '';
    const [anio, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${anio}`;
};

/** El lector devuelve dd/mm/aaaa; el <input type="date"> necesita aaaa-mm-dd. */
const aFechaISO = (espanola) => {
    const partes = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((espanola || '').trim());
    if (!partes) return '';
    const [, dia, mes, anio] = partes;
    return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
};

const nuevoProgenitor = () => ({
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    dni_nie: '',
    fecha_nacimiento: '',
    sexo: '',
    estado: '',
});

const nuevoHijo = () => ({
    archivo: '',
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    dni_nie: '',
    fecha_nacimiento: '',
    fecha_resolucion_adopcion: '',
    progenitor: nuevoProgenitor(),
});

const VALORES_INICIALES = {
    // Expediente: no va al PDF, es del office
    numero_negocio: '',
    // Solicitante: apellidos y nombre por separado, como pide el C-155
    primer_apellido: '',
    segundo_apellido: '',
    nombre: '',
    dni_nie: '',
    // Contacto: tampoco va al PDF, pero sí al expediente
    email: '',
    telefono: '',
    direccion: '',
    provincia: '',
    codigo_postal: '',
    // Complemento que se solicita
    complemento: 'brecha_genero',
    // Pensiones de las que es titular: independientes, se pueden marcar varias.
    // Jubilación viene marcada porque es el caso habitual del complemento.
    pension_jubilacion: true,
    pension_incapacidad_permanente: false,
    pension_viudedad: false,
    // Fecha en la que se jubilo el solicitante
    fecha_jubilacion: '',
    // Nota (1) del impreso
    progenitor_mismo_para_todos: true,
    // Pie: "En ..., a ... de ... de 20..."
    lugar: '',
    fecha_solicitud: hoyISO(),
};

const FICHEROS_INICIALES = {
    dni_frontal: null,
    dni_reverso: null,
    libro_familia: [],
    // No se lee ni se valida: se adjunta tal cual al final del PDF.
    resolucion: null,
};

/** ¿El lector ha sacado algún nombre de esta persona? */
const nombreCompleto = (persona) => (
    [persona?.nombre, persona?.primer_apellido, persona?.segundo_apellido].filter(Boolean).join(' ')
);

/** Persona leída del libro de familia -> hijo del formulario. */
const hijoDesdeLectura = (persona, progenitor) => ({
    ...nuevoHijo(),
    // De qué escaneo salió, para poder abrirlo desde su tarjeta.
    archivo: persona.archivo || '',
    nombre: persona.nombre || '',
    primer_apellido: persona.primer_apellido || '',
    segundo_apellido: persona.segundo_apellido || '',
    fecha_nacimiento: aFechaISO(persona.fecha_nacimiento),
    progenitor: progenitor || nuevoProgenitor(),
});

/**
 * Tarjeta de un hijo: sus datos y los del otro progenitor de ese hijo.
 *
 * Cuando el progenitor es el mismo para todos, el bloque solo se pinta en el
 * primer hijo para no teclearlo seis veces. Ojo: eso es cosa de la interfaz —
 * al enviar, sus datos se repiten en la columna de cada hijo, que es lo que
 * espera el impreso.
 */
const BloqueHijo = ({ hijo, indice, onChange, onEliminar, onCopiarProgenitor, onVerEscaneo, mostrarProgenitor, disabled }) => {
    const { t } = useTranslation();
    const h = `hijo${indice + 1}`;
    const p = `prog${indice + 1}`;
    const set = (campo) => (_, value) => onChange(indice, campo, value);
    const setProgenitor = (campo) => (_, value) => onChange(indice, `progenitor.${campo}`, value);

    return (
        <div className="col-span-1 sm:col-span-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('Hijo')} {indice + 1}
                </h4>
                <div className="flex items-center gap-4">
                    {hijo.archivo && (
                        <button
                            type="button"
                            onClick={() => onVerEscaneo(hijo.archivo)}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
                            title={hijo.archivo}
                        >
                            <PhotoIcon className="w-4 h-4" />
                            {t('Ver escaneo')}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onEliminar(indice)}
                        disabled={disabled}
                        className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                    >
                        <TrashIcon className="w-4 h-4" />
                        {t('Eliminar')}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField span={1} name={`${h}_nombre`} label={t('Nombre')} value={hijo.nombre} onChange={set('nombre')} disabled={disabled} />
                <TextField span={1} name={`${h}_primer_apellido`} label={t('Primer apellido')} value={hijo.primer_apellido} onChange={set('primer_apellido')} disabled={disabled} />
                <TextField span={1} name={`${h}_segundo_apellido`} label={t('Segundo apellido')} value={hijo.segundo_apellido} onChange={set('segundo_apellido')} disabled={disabled} />
                <TextField span={1} name={`${h}_dni_nie`} label="DNI / NIE" value={hijo.dni_nie} onChange={set('dni_nie')} disabled={disabled} />
                <DateField span={1} name={`${h}_fecha_nacimiento`} label={t('Fecha de nacimiento')} value={hijo.fecha_nacimiento} onChange={set('fecha_nacimiento')} disabled={disabled} />
                <DateField span={1} name={`${h}_fecha_resolucion_adopcion`} label={t('Fecha resolución adopción')} value={hijo.fecha_resolucion_adopcion} onChange={set('fecha_resolucion_adopcion')} disabled={disabled} />
            </div>

            {mostrarProgenitor && (
                <>
                    <div className="mt-6 mb-3 flex items-center justify-between gap-3">
                        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t('Otro progenitor de este hijo')}
                        </h5>
                        {indice > 0 && (
                            <button
                                type="button"
                                onClick={() => onCopiarProgenitor(indice)}
                                disabled={disabled}
                                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400 disabled:opacity-50"
                            >
                                <DocumentDuplicateIcon className="w-4 h-4" />
                                {t('Copiar del hijo anterior')}
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <SelectField
                            name={`${p}_estado`}
                            label={t('¿No existe o ha fallecido?')}
                            value={hijo.progenitor.estado}
                            onChange={setProgenitor('estado')}
                            options={ESTADOS_PROGENITOR.map((o) => ({ ...o, label: t(o.label) }))}
                            placeholder={t('Existe (rellenar sus datos)')}
                            disabled={disabled}
                        />
                        <TextField span={1} name={`${p}_nombre`} label={t('Nombre')} value={hijo.progenitor.nombre} onChange={setProgenitor('nombre')} disabled={disabled} />
                        <TextField span={1} name={`${p}_primer_apellido`} label={t('Primer apellido')} value={hijo.progenitor.primer_apellido} onChange={setProgenitor('primer_apellido')} disabled={disabled} />
                        <TextField span={1} name={`${p}_segundo_apellido`} label={t('Segundo apellido')} value={hijo.progenitor.segundo_apellido} onChange={setProgenitor('segundo_apellido')} disabled={disabled} />
                        <TextField span={1} name={`${p}_dni_nie`} label="DNI / NIE" value={hijo.progenitor.dni_nie} onChange={setProgenitor('dni_nie')} disabled={disabled} />
                        <DateField span={1} name={`${p}_fecha_nacimiento`} label={t('Fecha de nacimiento')} value={hijo.progenitor.fecha_nacimiento} onChange={setProgenitor('fecha_nacimiento')} disabled={disabled} />
                        <SelectField span={1} name={`${p}_sexo`} label={t('Sexo')} value={hijo.progenitor.sexo} onChange={setProgenitor('sexo')} options={SEXOS} placeholder="—" disabled={disabled} />
                    </div>
                </>
            )}
        </div>
    );
};

BloqueHijo.propTypes = {
    hijo: propTypes.object.isRequired,
    indice: propTypes.number.isRequired,
    onChange: propTypes.func.isRequired,
    onEliminar: propTypes.func.isRequired,
    onCopiarProgenitor: propTypes.func.isRequired,
    onVerEscaneo: propTypes.func.isRequired,
    mostrarProgenitor: propTypes.bool,
    disabled: propTypes.bool,
};

/**
 * Documento de Brecha de Género (impreso C-155: complemento para la reducción
 * de la brecha de género).
 *
 * Dos pasos:
 *   1. Subir las páginas del libro de familia. El backend las pasa por el
 *      workflow de n8n, que las lee y devuelve los integrantes clasificados.
 *   2. Revisar y completar el formulario, y generar el PDF.
 *
 * El paso 1 se puede saltar y rellenarlo todo a mano.
 */
export const FormBrechaGenero = ({ setCurrentStep }) => {
    const { t } = useTranslation();

    const [paso, setPaso] = useState('subida');
    const [lectura, setLectura] = useState(null);
    const [lecturaDni, setLecturaDni] = useState(null);
    const [values, setValues] = useState(VALORES_INICIALES);
    const [hijos, setHijos] = useState([nuevoHijo()]);
    const [files, setFiles] = useState(FICHEROS_INICIALES);
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [dniLoading, setDniLoading] = useState(false);
    const [negocioLoading, setNegocioLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [visorIndice, setVisorIndice] = useState(null);

    const direccionRef = useRef();

    /**
     * Los documentos subidos, listos para el visor. Las URLs de objeto se
     * revocan al cambiar los ficheros: si no, cada revisión deja los escaneos
     * retenidos en memoria durante toda la sesión.
     */
    const imagenes = useMemo(() => {
        const lista = files.libro_familia.map((file) => ({ file, nombre: file.name, etiqueta: file.name }));
        if (files.dni_frontal) lista.push({ file: files.dni_frontal, nombre: files.dni_frontal.name, etiqueta: 'DNI anverso' });
        if (files.dni_reverso) lista.push({ file: files.dni_reverso, nombre: files.dni_reverso.name, etiqueta: 'DNI reverso' });
        return lista.map((imagen) => ({ ...imagen, url: URL.createObjectURL(imagen.file) }));
    }, [files]);

    useEffect(() => () => imagenes.forEach(({ url }) => URL.revokeObjectURL(url)), [imagenes]);

    /** Abre el visor en el escaneo del que salió una ficha concreta. */
    const verEscaneo = (archivo) => {
        const i = imagenes.findIndex((imagen) => imagen.nombre === archivo);
        setVisorIndice(i === -1 ? 0 : i);
    };

    const setValue = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));
    const setFile = (name, file) => setFiles((prev) => ({ ...prev, [name]: file }));

    // El input de dirección no existe mientras estamos en el paso de subida:
    // hay que reenganchar el autocompletado cuando aparece el formulario.
    useAddressAutocomplete(direccionRef, ({ direccion, provincia, codigo_postal }) => {
        setValues((prev) => ({
            ...prev,
            direccion,
            provincia: provincia || prev.provincia,
            codigo_postal: codigo_postal || prev.codigo_postal,
            // El "En ..." del pie suele ser la misma provincia; solo si está vacío.
            lugar: prev.lugar || provincia || '',
        }));
    }, [paso, success]);

    // --- Paso 1: lo que ha leído el libro de familia -----------------------

    const aplicarLectura = ({ libro, dni }, { paginas, dniFrontal, dniReverso }) => {
        setLectura(libro);
        setLecturaDni(dni);

        const solicitante = libro?.solicitante || {};
        setValues((prev) => ({
            ...prev,
            nombre: solicitante.nombre || prev.nombre,
            primer_apellido: solicitante.primer_apellido || prev.primer_apellido,
            segundo_apellido: solicitante.segundo_apellido || prev.segundo_apellido,
        }));

        // El lector devuelve un único otro progenitor, que va en la columna del
        // primer hijo: es exactamente el supuesto de la nota (1) del impreso.
        const otro = libro?.otro_progenitor || {};
        const progenitorLeido = nombreCompleto(otro) ? {
            ...nuevoProgenitor(),
            nombre: otro.nombre || '',
            primer_apellido: otro.primer_apellido || '',
            segundo_apellido: otro.segundo_apellido || '',
            fecha_nacimiento: aFechaISO(otro.fecha_nacimiento),
            sexo: otro.sexo || '',
        } : nuevoProgenitor();

        const leidos = libro?.hijos || [];
        setHijos(leidos.length > 0
            ? leidos.slice(0, MAX_HIJOS).map((hijo, i) => hijoDesdeLectura(hijo, i === 0 ? progenitorLeido : undefined))
            : [{ ...nuevoHijo(), progenitor: progenitorLeido }]);

        if (leidos.length > MAX_HIJOS) {
            toast.error(t('El impreso solo admite 6 hijos: se han descartado los sobrantes'));
        }

        // El DNI manda sobre el libro de familia para la identidad del
        // solicitante: es el documento oficial y trae el número, que el libro no.
        if (dni?.encontrado) {
            setValues((prev) => ({
                ...prev,
                dni_nie: dni.dni_nie || prev.dni_nie,
                nombre: dni.nombre || prev.nombre,
                primer_apellido: dni.primer_apellido || prev.primer_apellido,
                segundo_apellido: dni.segundo_apellido || prev.segundo_apellido,
                direccion: dni.direccion || prev.direccion,
                codigo_postal: dni.codigo_postal || prev.codigo_postal,
                provincia: dni.provincia || prev.provincia,
                lugar: prev.lugar || dni.provincia || '',
            }));
        } else if (dni) {
            toast(t('No se ha podido leer el DNI: rellena sus datos a mano'), { icon: '⚠️' });
        }

        setFiles((prev) => ({
            ...prev,
            libro_familia: paginas,
            dni_frontal: dniFrontal || prev.dni_frontal,
            dni_reverso: dniReverso || prev.dni_reverso,
        }));
        setPaso('formulario');
    };

    // --- Hijos -------------------------------------------------------------

    const actualizarHijo = (indice, campo, value) => {
        setHijos((prev) => prev.map((hijo, i) => {
            if (i !== indice) return hijo;
            if (campo.startsWith('progenitor.')) {
                return { ...hijo, progenitor: { ...hijo.progenitor, [campo.slice('progenitor.'.length)]: value } };
            }
            return { ...hijo, [campo]: value };
        }));
    };

    const anadirHijo = () => {
        if (hijos.length >= MAX_HIJOS) {
            toast.error(t('El impreso solo admite 6 hijos'));
            return;
        }
        setHijos((prev) => [...prev, nuevoHijo()]);
    };

    const eliminarHijo = (indice) => {
        setHijos((prev) => (prev.length === 1 ? [nuevoHijo()] : prev.filter((_, i) => i !== indice)));
    };

    /**
     * Copia el otro progenitor del hijo anterior. Para el caso de hermanos de
     * distinta madre: se repiten los que comparten y se teclea solo el que cambia.
     */
    const copiarProgenitorAnterior = (indice) => {
        if (indice === 0) return;
        setHijos((prev) => prev.map((hijo, i) => (
            i === indice ? { ...hijo, progenitor: { ...prev[indice - 1].progenitor } } : hijo
        )));
        toast.success(t('Progenitor copiado del hijo anterior'));
    };

    // --- Autocompletado desde Baserow --------------------------------------

    /** Vuelca la ficha personal de Baserow (tabla Datos Personales, 871). */
    const rellenarDesdeBaserow = (persona) => {
        if (!persona) return;
        setValues((prev) => ({
            ...prev,
            dni_nie: persona['DNI'] || prev.dni_nie,
            nombre: persona['Nombre'] || prev.nombre,
            primer_apellido: persona['Primer Apellido'] || prev.primer_apellido,
            segundo_apellido: persona['Segundo Apellido'] || prev.segundo_apellido,
            email: persona['Email'] || prev.email,
            telefono: persona['Telefono'] || prev.telefono,
            direccion: persona['Direccion'] || prev.direccion,
            provincia: persona['Provincia'] || prev.provincia,
            codigo_postal: persona['Codigo Postal'] || prev.codigo_postal,
            lugar: prev.lugar || persona['Provincia'] || '',
        }));

        // Las fichas antiguas solo tienen el nombre junto: el C-155 lo quiere partido.
        if (!persona['Primer Apellido'] && persona['Nombre Y Apellidos']) {
            toast(t('La ficha no tiene el nombre desglosado: revisa apellidos y nombre'), { icon: '⚠️' });
        }
    };

    const buscarPorDni = async (dni) => {
        const valor = (dni || '').trim();
        if (!valor) return;

        if (!validarDNIoNIF(valor)) {
            toast.error(t('El DNI introducido no es válido'));
            return;
        }

        try {
            setDniLoading(true);
            const datos = await buscarDNIEnBaserow(valor, 'BrechaGenero');
            if (datos && datos.length > 0) rellenarDesdeBaserow(datos[0]);
        } catch (error) {
            console.error('Error al buscar DNI:', error);
            toast.error(t('Ha ocurrido un error al buscar el DNI'));
        } finally {
            setDniLoading(false);
        }
    };

    /**
     * El número de negocio se busca en HubSpot, no en Baserow: el DNI sale de una
     * propiedad del negocio, y el email y el teléfono del contacto asociado.
     * El nombre y los apellidos del solicitante vienen del libro de familia.
     */
    const buscarPorNumeroNegocio = async () => {
        if (!values.numero_negocio) {
            toast.error(t('Introduce un número de negocio'));
            return;
        }
        try {
            setNegocioLoading(true);
            const negocio = await buscarNegocioEnHubspot(values.numero_negocio);

            if (!negocio?.encontrado) {
                toast.error(t('No se ha encontrado ese número de negocio en HubSpot'));
                return;
            }

            setValues((prev) => ({
                ...prev,
                dni_nie: negocio.dni_nie || prev.dni_nie,
                email: negocio.email || prev.email,
                telefono: negocio.telefono || prev.telefono,
            }));

            // El DNI es obligatorio para el impreso y es lo único que no se puede
            // sacar ni del libro de familia ni de ningún otro sitio.
            if (!negocio.dni_nie) {
                toast(t('El negocio no tiene DNI en HubSpot: tecléalo a mano'), { icon: '⚠️' });
            }
            toast.success(t('Datos de HubSpot introducidos'));
        } catch (error) {
            console.error('Error al buscar el número de negocio:', error);
            toast.error(t('Ha ocurrido un error al buscar el número de negocio'));
        } finally {
            setNegocioLoading(false);
        }
    };

    // --- Envío -------------------------------------------------------------

    /** Un bloque de hijo en blanco significa "este hijo no existe". */
    const hijosConDatos = () => hijos.filter((hijo) => (
        hijo.nombre || hijo.primer_apellido || hijo.segundo_apellido ||
        hijo.dni_nie || hijo.fecha_nacimiento || hijo.fecha_resolucion_adopcion
    ));

    const validarAntesDeEnviar = () => {
        if (!validarDNIoNIF(values.dni_nie)) {
            toast.error(t('El DNI introducido no es válido'));
            return false;
        }
        if (values.telefono && !validarTelefono(values.telefono)) {
            toast.error(t('El teléfono introducido no es válido'));
            return false;
        }
        if (!values.pension_jubilacion && !values.pension_incapacidad_permanente && !values.pension_viudedad) {
            toast.error(t('Marca al menos una pensión de la que sea titular'));
            return false;
        }
        if (hijosConDatos().length === 0) {
            toast.error(t('Añade al menos un hijo'));
            return false;
        }
        return true;
    };

    /** El JSON del impreso, con la misma forma que espera el generador del C-155. */
    const construirDatosC155 = () => {
        const rellenados = hijosConDatos();
        const [anio, mes, dia] = (values.fecha_solicitud || '').split('-');

        const progenitores = rellenados.map(({ progenitor }) => ({
            ...progenitor,
            fecha_nacimiento: aFechaEspanola(progenitor.fecha_nacimiento),
        }));

        return {
            solicitante: {
                primer_apellido: values.primer_apellido,
                segundo_apellido: values.segundo_apellido,
                nombre: values.nombre,
                dni_nie: values.dni_nie,
            },
            complemento: values.complemento,
            pension_jubilacion: values.pension_jubilacion,
            pension_incapacidad_permanente: values.pension_incapacidad_permanente,
            pension_viudedad: values.pension_viudedad,
            // Un campo por cada fila de la rejilla de hijos del impreso.
            hijos: rellenados.map((hijo) => ({
                nombre: hijo.nombre,
                primer_apellido: hijo.primer_apellido,
                segundo_apellido: hijo.segundo_apellido,
                dni_nie: hijo.dni_nie,
                fecha_nacimiento: aFechaEspanola(hijo.fecha_nacimiento),
                fecha_resolucion_adopcion: aFechaEspanola(hijo.fecha_resolucion_adopcion),
            })),
            // El impreso quiere una columna de otro progenitor por cada hijo. Si es
            // el mismo para todos, sus datos se REPITEN en todas las columnas: se
            // teclea una vez, pero viajan tantas veces como hijos haya.
            otro_progenitor: values.progenitor_mismo_para_todos
                ? rellenados.map(() => progenitores[0])
                : progenitores,
            ...CONSENTIMIENTOS,
            fecha_jubilacion: aFechaEspanola(values.fecha_jubilacion),
            lugar: values.lugar,
            dia: dia ? String(Number(dia)) : '',
            mes: mes ? MESES[Number(mes) - 1] : '',
            anio: anio ? anio.slice(-2) : '',
        };
    };

    const construirFormData = () => {
        const formData = new FormData();

        // Campos planos: los que usa el office para el expediente.
        formData.append('tipo_formulario', TIPO_FORMULARIO);
        ['numero_negocio', 'email', 'telefono', 'direccion', 'provincia', 'codigo_postal']
            .forEach((name) => formData.append(name, values[name] ?? ''));

        // El impreso entero, en un único JSON.
        formData.append('datos', JSON.stringify(construirDatosC155()));

        if (files.dni_frontal) formData.append('dni_frontal', files.dni_frontal, files.dni_frontal.name);
        if (files.resolucion) formData.append('resolucion', files.resolucion, files.resolucion.name);
        if (files.dni_reverso) formData.append('dni_reverso', files.dni_reverso, files.dni_reverso.name);
        files.libro_familia.forEach((f) => formData.append('libro_familia', f, f.name));

        return formData;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validarAntesDeEnviar()) return;

        setLoading(true);
        try {
            const generados = await descargarGenerados(await generarDocumentos(construirFormData()));
            toast.success(t('¡Datos descargados correctamente!'));

            // A revisar antes de mandarlos a firma: el agente los previsualiza y,
            // si alguno ha salido mal, lo sustituye sin rehacer el formulario.
            setDocumentos(generados);
            setPaso('revision');
        } catch (error) {
            console.error(error);
            toast.error(t('Ha ocurrido un error inesperado'));
        } finally {
            setLoading(false);
        }
    };

    /** Nombre completo del solicitante, tal y como lo quiere la API de firma. */
    const nombreDelSolicitante = () => (
        [values.nombre, values.primer_apellido, values.segundo_apellido].filter(Boolean).join(' ')
    );

    /** Sustituye un documento generado por la version corregida del agente. */
    const reemplazarDocumento = (indice, fichero) => {
        setDocumentos((prev) => prev.map((documento, i) => (
            // Se conserva el nombre generado: es lo que identifica al documento
            // en el envio a firma, y asi el workflow siempre recibe lo mismo.
            i === indice ? { ...documento, blob: fichero, reemplazado: fichero.name } : documento
        )));
        toast.success(t('Documento sustituido'));
    };

    const handleEnviarAFirma = async () => {
        setEnviando(true);
        try {
            // El .docx no viaja: es la copia editable del contrato, no el
            // documento que se firma.
            await enviarAFirma({
                idNegocio: values.numero_negocio,
                nombreCliente: nombreDelSolicitante(),
                emailCliente: values.email,
                localidad: values.lugar,
            }, documentos.filter((d) => esPdf(d.nombre)));
            setSuccess(true);
        } catch (error) {
            console.error('Error al enviar a firma:', error);
            toast.error(t('No se ha podido enviar a firma. Los documentos siguen descargados en tu equipo.'));
        } finally {
            setEnviando(false);
        }
    };

    const reiniciar = () => {
        setValues({ ...VALORES_INICIALES, fecha_solicitud: hoyISO() });
        setHijos([nuevoHijo()]);
        setFiles(FICHEROS_INICIALES);
        setLectura(null);
        setLecturaDni(null);
        setDocumentos([]);
        setSuccess(false);
        setPaso('subida');
    };

    // --- Pantallas ---------------------------------------------------------

    if (success) {
        return (
            <div className="flex flex-col items-center justify-center mt-12">
                <svg className="w-12 h-12 text-gray-800 dark:text-white mb-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">{t('Documentos generados correctamente')}</h3>
                <p className="text-gray-600 dark:text-gray-400">{t('Los documentos se han descargado en tu equipo')}</p>
                <button
                    type="button"
                    className="mt-10 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300"
                    onClick={reiniciar}
                >
                    {t('¡Listo!')}
                </button>
            </div>
        );
    }

    if (paso === 'revision') {
        return (
            <RevisionDocumentos
                documentos={documentos}
                onReemplazar={reemplazarDocumento}
                onEnviar={handleEnviarAFirma}
                onListo={() => setSuccess(true)}
                onBack={() => setPaso('formulario')}
                enviando={enviando}
                puedeEnviar={Boolean(values.numero_negocio) && documentos.some((d) => esPdf(d.nombre))}
            />
        );
    }

    if (paso === 'subida') {
        return (
            <SubidaLibroFamilia
                onAnalizado={aplicarLectura}
                onSaltar={() => setPaso('formulario')}
                onBack={() => setCurrentStep(1)}
            />
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <FormHeader
                emoji="⚖️"
                title={t('Rellenando documento de')}
                highlight={t('Brecha de Género')}
                onBack={() => setCurrentStep(1)}
                backLabel={t('Volver atrás')}
            />

            <PanelDocumentos imagenes={imagenes} onAbrir={setVisorIndice} />

            <VisorImagenes
                imagenes={imagenes}
                indice={visorIndice}
                onCerrar={() => setVisorIndice(null)}
                onCambiar={setVisorIndice}
            />

            <ResumenLectura
                lectura={lectura}
                lecturaDni={lecturaDni}
                onVolverASubir={() => setPaso('subida')}
            />

            <FormSection
                title={t('Datos del expediente')}
                description={t('Trae de HubSpot el DNI del negocio, y el email y el teléfono del contacto asociado')}
            >
                <SearchField
                    name="numero_negocio"
                    label={t('Número de negocio')}
                    value={values.numero_negocio}
                    onChange={setValue}
                    onSearch={buscarPorNumeroNegocio}
                    buttonLabel={t('Buscar')}
                    disabled={loading}
                    loading={negocioLoading}
                    placeholder={t('Buscar un número de negocio...')}
                />
            </FormSection>

            <FormSection title={t('Datos del solicitante')}>
                <TextField
                    span={1}
                    name="dni_nie"
                    label="DNI / NIE"
                    value={values.dni_nie}
                    onChange={setValue}
                    onBlur={buscarPorDni}
                    placeholder="12345678Z"
                    disabled={loading}
                    loading={dniLoading}
                    required
                />
                <TextField span={1} name="nombre" label={t('Nombre')} value={values.nombre} onChange={setValue} disabled={loading} required />
                <TextField span={1} name="primer_apellido" label={t('Primer apellido')} value={values.primer_apellido} onChange={setValue} disabled={loading} required />
                <TextField span={1} name="segundo_apellido" label={t('Segundo apellido')} value={values.segundo_apellido} onChange={setValue} disabled={loading} />
            </FormSection>

            <FormSection
                title={t('Datos de contacto')}
                description={t('No van al impreso, pero quedan en el expediente')}
            >
                <TextField span={1} name="email" label="Email" type="email" value={values.email} onChange={setValue} placeholder="Email" disabled={loading} />
                <TextField
                    span={1}
                    name="telefono"
                    label={t('Teléfono')}
                    value={values.telefono}
                    onChange={setValue}
                    onBlur={(valor) => {
                        if (valor && !validarTelefono(valor)) toast.error(t('El teléfono introducido no es válido'));
                    }}
                    placeholder={t('Teléfono')}
                    disabled={loading}
                />
                <TextField
                    name="direccion"
                    label={t('Dirección')}
                    value={values.direccion}
                    onChange={setValue}
                    inputRef={direccionRef}
                    placeholder={t('Dirección')}
                    disabled={loading}
                />
                <TextField span={1} name="provincia" label={t('Provincia')} value={values.provincia} onChange={setValue} placeholder={t('Provincia')} disabled={loading} />
                <TextField span={1} name="codigo_postal" label={t('Código Postal')} value={values.codigo_postal} onChange={setValue} placeholder={t('Código Postal')} disabled={loading} />
            </FormSection>

            <FormSection title={t('Complemento que se solicita')}>
                <SelectField
                    name="complemento"
                    label={t('Complemento')}
                    value={values.complemento}
                    onChange={setValue}
                    options={COMPLEMENTOS.map((o) => ({ ...o, label: t(o.label) }))}
                    disabled={loading}
                    required
                    hint={t('Son excluyentes: el impreso solo deja marcar uno')}
                />
            </FormSection>

            <FormSection
                title={t('Pensión de la que es titular')}
                description={t('Puedes marcar más de una')}
            >
                <CheckboxField name="pension_jubilacion" label={t('Jubilación')} checked={values.pension_jubilacion} onChange={setValue} disabled={loading} />
                <CheckboxField name="pension_incapacidad_permanente" label={t('Incapacidad permanente')} checked={values.pension_incapacidad_permanente} onChange={setValue} disabled={loading} />
                <CheckboxField name="pension_viudedad" label={t('Viudedad')} checked={values.pension_viudedad} onChange={setValue} disabled={loading} />
                <DateField
                    name="fecha_jubilacion"
                    label={t('Fecha de jubilación')}
                    value={values.fecha_jubilacion}
                    onChange={setValue}
                    disabled={loading}
                    hint={t('Si se deja vacía, ese hueco del impreso sale en blanco')}
                />
            </FormSection>

            <FormSection title={t('Hijos y otro progenitor')}>
                <CheckboxField
                    name="progenitor_mismo_para_todos"
                    label={t('El otro progenitor es el mismo para todos los hijos')}
                    description={t('Lo tecleas una vez y sus datos se copian en la columna de cada hijo del impreso')}
                    checked={values.progenitor_mismo_para_todos}
                    onChange={setValue}
                    disabled={loading}
                />

                {hijos.map((hijo, indice) => (
                    <BloqueHijo
                        key={indice}
                        hijo={hijo}
                        indice={indice}
                        onChange={actualizarHijo}
                        onEliminar={eliminarHijo}
                        onCopiarProgenitor={copiarProgenitorAnterior}
                        onVerEscaneo={verEscaneo}
                        mostrarProgenitor={!values.progenitor_mismo_para_todos || indice === 0}
                        disabled={loading}
                    />
                ))}

                <div className="col-span-1 sm:col-span-2 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={anadirHijo}
                        disabled={loading || hijos.length >= MAX_HIJOS}
                        className="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PlusIcon className="w-4 h-4" />
                        {t('Añadir hijo')}
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{hijos.length} / {MAX_HIJOS}</span>
                </div>
            </FormSection>

            <FormSection
                title={t('Documentación que se adjunta')}
                description={t('Se añaden al final del PDF en páginas nuevas: primero el DNI, luego el libro de familia y por último la resolución')}
            >
                <FileField span={1} name="dni_frontal" label={t('DNI / NIE · anverso')} accept="image/*" file={files.dni_frontal} onChange={setFile} disabled={loading} />
                <FileField span={1} name="dni_reverso" label={t('DNI / NIE · reverso')} accept="image/*" file={files.dni_reverso} onChange={setFile} disabled={loading} />
                <FileField
                    name="resolucion"
                    label={t('Resolución')}
                    accept="image/*,.pdf"
                    file={files.resolucion}
                    onChange={setFile}
                    disabled={loading}
                    hint={t('No se lee ni se valida: se adjunta tal cual al final del PDF. Admite imagen o PDF de varias páginas')}
                />
                <FileListField
                    name="libro_familia"
                    label={t('Libro de familia')}
                    accept="image/*"
                    files={files.libro_familia}
                    onChange={setFile}
                    disabled={loading}
                    hint={t('Las páginas del paso anterior ya están aquí: usa el campo solo para añadir alguna más')}
                    vacio={t('Todavía no has seleccionado ninguna página')}
                />
            </FormSection>

            <FormSection title={t('Lugar y fecha')}>
                <TextField span={1} name="lugar" label={t('Lugar')} value={values.lugar} onChange={setValue} placeholder={t('Localidad')} disabled={loading} />
                <DateField span={1} name="fecha_solicitud" label={t('Fecha de la solicitud')} value={values.fecha_solicitud} onChange={setValue} disabled={loading} />
            </FormSection>

            <div className="mt-10">
                <SubmitButton loading={loading} label={t('Generar documentos')} loadingLabel={t('Generando...')} />
            </div>
        </form>
    );
};

FormBrechaGenero.propTypes = {
    setCurrentStep: propTypes.func.isRequired,
};
